// Angular
import { Injectable, inject, DOCUMENT, signal } from "@angular/core";

// RxJs & Lodash
import { find } from "lodash";
// Utils
import { CommonService } from '@core/utils';
// Constants
import { ENV_CUSTOMER_COUNTRY_CODE, ENV_CUSTOMER_COUNTRY_ID, ENV_CUSTOMER_COUNTRY_NAME, ENV_CUSTOMER_CURRENCY, ENV_CUSTOMER_LANG, ENV_CUSTOMER_LANG_CODE, ENV_CUSTOMER_LANG_NAME, ENV_CUSTOMER_TYPE, LOCALES } from '@constants';
// Transloco
import { TranslocoService } from "@jsverse/transloco";
// Environment
import { environment } from "@environment";

@Injectable({
    providedIn: 'root',
})
export class LayoutService {

    // Private
    private readonly commonService = inject(CommonService);
    private readonly translocoService = inject(TranslocoService);
    private document = inject<Document>(DOCUMENT);

    // Signal
    public customerTypeSignal = signal<string>(this.getCustomerType());

    /**
     * Set Customer Type
     * @param {string} type
     * @memberof LayoutService
     */
    public setCustomerType(type: string): void {
        this.commonService.setIntoLocalStorage(ENV_CUSTOMER_TYPE, type);
        this.customerTypeSignal.set(type); 
    }

    /**
     * Set Customer Language
     * @param {*} language
     * @memberof LayoutService
     */
    public setCustomerLanguage(language: any): void {
        this.commonService.setIntoLocalStorage(ENV_CUSTOMER_LANG, language?.locale);
        this.commonService.setIntoLocalStorage(ENV_CUSTOMER_LANG_CODE, language?.code);
        this.commonService.setIntoLocalStorage(ENV_CUSTOMER_LANG_NAME, language?.language);
    }

    /**
     * Set Customer Country
     * @param {*} country
     * @memberof LayoutService
     */
    public setCustomerCountry(country: any): void {
        this.commonService.setIntoLocalStorage(ENV_CUSTOMER_COUNTRY_CODE, country?.code);
        this.commonService.setIntoLocalStorage(ENV_CUSTOMER_COUNTRY_ID, country?.id);
        this.commonService.setIntoLocalStorage(ENV_CUSTOMER_COUNTRY_NAME, country?.flag);
    }

    /**
     * Set Customer Currency
     * @param {string} currency
     * @memberof LayoutService
     */
    public setCustomerCurrency(currency: string): void {
        this.commonService.setIntoLocalStorage(ENV_CUSTOMER_CURRENCY, currency);
    }

    /**
     * Get Customer Type
     * @returns {string}
     * @memberof LayoutService
     */
    public getCustomerType(): string {
        const customerType = this.commonService.getFromLocalStorage(ENV_CUSTOMER_TYPE);
        return customerType ? customerType : '1';
    }

    /**
     * Get Customer Language
     * @returns {string}
     * @memberof LayoutService
     */
    public getCustomerLanguage(): string {
        const customerLanguage = this.commonService.getFromLocalStorage(ENV_CUSTOMER_LANG);
        return customerLanguage ? customerLanguage : this.translocoService.getActiveLang();
    }

    /**
     * Get Customer Language Object
     * @returns
     * @memberof LayoutService
     */
    public getCustomerLanguageCode() {
        const customerLanguage = this.commonService.getFromLocalStorage(ENV_CUSTOMER_LANG);
        return customerLanguage ? find(LOCALES, (l) => l.locale === customerLanguage) : find(LOCALES, (l) => l.locale === this.translocoService.getActiveLang());
    }

    /**
     * Get Customer Currency
     * @returns {string}
     */
    public getCustomerCurrency(): string {
        const currency = this.commonService.getFromLocalStorage(ENV_CUSTOMER_CURRENCY);
        if (currency) return currency;

        const locale = this.extractLocaleFromPath();
        return this.commonService.getUserLocale(locale).currency;
    }

    /**
     * Get Current Country Info
     * @returns { { code: string, id: string, name: string } }
     */
    public getCurrentCountry() {
        const code = this.commonService.getFromLocalStorage(ENV_CUSTOMER_COUNTRY_CODE);
        const name = this.commonService.getFromLocalStorage(ENV_CUSTOMER_COUNTRY_NAME);
        const id = this.commonService.getFromLocalStorage(ENV_CUSTOMER_COUNTRY_ID);

        if (id && code && name) {
            return { code, id, name };
        }

        const locale = this.extractLocaleFromPath();
        const country = this.commonService.getUserLocale(locale);
        return { code: country.code, id: country.id, name: country.name };
    }

    /**
     * Extracts the locale segment from the URL path (e.g., 'sv-se' from '/sv-se/xyz')
     * Falls back to default locale from environment
     * @returns {string}
     */
    public extractLocaleFromPath(): string {
        const path = this.document.location?.pathname ?? '';
        const cleanPath = path.replace(/^\/+/, ''); // Remove leading slashes
        const firstSegment = cleanPath.split('/')[0];
        return firstSegment || environment.visitorSettings.defaultLocale;
    }

}